import React, { useState, useReducer, useEffect } from 'react';
import axios from 'axios';
import { PropTypes } from 'prop-types';
import AsyncSelect from 'react-select/async';
import ReactSelect from 'react-select';

import DeleteModal from '../../Modal/DeleteModal';
import DateAndTimePicker from '../../Form/DateAndTimePicker';

import slugify from '../../../utils/slugify';
import TIMEZONES from '../../../utils/timezones';
import LanguageTabsForms from '../../Tabs/LanguageTabsForms';

import Toggle from '../DragAndDrop/ItemsComponents/ui/toggle';
import UIImage from '../DragAndDrop/ItemsComponents/ui/UIImage';

import searchPosts from '../../../api/graphql/queries/searchPosts';
import searchSlugsByCollection from '../../../api/graphql/queries/searchSlugsByCollection';
import artistSetsByArtistSetSlugs from '../../../api/graphql/queries/artistSetsByArtistSetSlugs';
import getUsersList from '../../../api/graphql/queries/getUsersList';
import reducer from '../../../store/formReducer';
import { init, setError, setValue, setValueLocalized, clearError } from '../../../store/actions';
import { GENRE_OPTIONS } from '../../../utils/consts';


// TODO: add proptypes and move subcomponents to single files
const initialState = {
  isEdit: false,
  error: {},
  data: {
    artistSets: [],
    eventSlugName: '',
    eventName: '',
    eventReadableName: {},
    subtitle: {},
    description: {},
    pretitle: {},
    image: {},
    postSlugNames: [],
    tickets: [],
    totalCapacity: 0,
    venues: [],
    endDateTime: null,
    beginDateTime: null,
    showDate: false,
    showDateTime: false,
    showTicket: false,
    showVenue: false,
    genres: [],
    timezone: 'Europe/Madrid',
    mentors: [],
    salePostSlugName: 'tickets',
  },
};

const MENTOR_ROLE_MUSIC_MEETS = 'pro-mentor-2022';

const isValidSlugName = async (value) => {
  const getEventsSlugQueryResult = await searchSlugsByCollection(value, 'events', true);
  return getEventsSlugQueryResult?.slugNames?.length === 0;
};

const getVenueFromArtistSet = async (artistSets = []) => {
  if (artistSets.length === 0) return [];
  try {
    const res = await artistSetsByArtistSetSlugs(artistSets);
    if (res?.length) {
      return [...new Set(res.map(artistSet => artistSet.venueSlugName))];
    }
    return [];
  } catch (error) {
    console.log('getVenuesFromArtistSet error', error);
    return [];
  }
};

const useEvent = (event) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { data, error } = state;

  useEffect(() => {
    if (event) {
      init(dispatch)(event);
    }
  }, []);

  const checkValidSlugName = async () => {
    if (data.eventName) {
      const isValid = await isValidSlugName(data.eventName);
      if (!isValid) {
        setError(dispatch)({
          eventName: 'This eventName already exists, try a new one',
        });
        return;
      }
      clearError(dispatch)('eventSlugName');
      setValue(dispatch)('eventSlugName', slugify(data.eventName).toLowerCase());
    }
  };

  useEffect(() => {
    getVenueFromArtistSet(data.artistSets).then(res => setValue(dispatch)('venues', res));
  }, [data.artistSets]);

  return { state, dispatch, checkValidSlugName };
};

const Input = ({ error, label, name, onChange, type, value, ...otherProps }) => (
  <div className="w-full">
    <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
      {label}
      <input
        id={name}
        onChange={onChange}
        name={name}
        type={type || 'text'}
        value={value || ''}
        className={`mt-2 rounded-full border-1 p-2 bg-white outline-none ${error ? ' text-red border-red border-2' : ' border-black text-black '}`}
        {...otherProps}
      />
    </label>
  </div>
);

const Textarea = ({ label, name, onChange, value, ...otherProps }) => (
  <div className="w-full">
    <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
      {label}
      <textarea className="font-americaMonoRegular p-2 bg-white outline-none w-full mt-2 rounded-lg h-28" onChange={onChange} name={name} type="text" value={value} {...otherProps} />
    </label>
  </div>
);

const Spacer = () => <div className="m-4" />;

const transformOptions = (posts) => {
  if (!Array.isArray(posts)) return [];
  return posts.map(post => ({ label: post.slugName, value: post.slugName }));
};

const transformSlugnamesOptions = (options) => {
  const { slugNames } = options;
  if (!Array.isArray(slugNames)) return [];
  return slugNames.map(slugName => ({ label: slugName, value: slugName }));
};

const transformToSelectData = (options) => {
  if (!Array.isArray(options)) return [];
  return options.map(option => ({ label: option, value: option }));
};


const transformUserOptions = (options) => {
  const { users } = options;
  if (!Array.isArray(users)) return [];
  return users.map(user => ({ label: user.email, value: user._id }));
};

const Select = ({ className, defaultOptions, isLoading, onChange, label, loadOptions, name, value }) => (
  <div className={`w-full ${className}`}>
    <label className="font-americaMonoRegular uppercase text-xs" htmlFor={name}>
      {label}
      <AsyncSelect
        className="font-americaMonoRegular text-black font-bold mt-1"
        isMulti
        id={name}
        name={name}
        cacheOptions
        value={value}
        isLoading={isLoading}
        loadOptions={loadOptions}
        defaultOptions={defaultOptions}
        onChange={onChange}
      />
    </label>
  </div>
);

const getPosts = value => searchPosts(value).then(res => transformOptions(res)).catch(() => []);
const getSlugsNames = (name, value) => searchSlugsByCollection(value, name).then(res => transformSlugnamesOptions(res)).catch(() => []);

const EventForm = ({ canItemBeDeleted, formInput, handleDelete, saveEvent }) => {
  const { checkValidSlugName, state: { error, data, isEdit }, dispatch } = useEvent(formInput);
  const [mentorsDictionary, setMentorsDictionary] = useState();
  const [creatingLineup, setCreatingLineup] = useState(false);
  const [mentors, setMentors] = useState([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const validateForm = () => {
    const errors = {};
    if (!data.eventName) {
      errors.eventName = 'Event name is required';
    }
    if (!data.eventSlugName) {
      errors.eventSlugName = 'Event slug name is required';
    }
    if (!data.eventReadableName?.en) {
      errors.eventReadableName = 'Event readable name in English is required';
    }

    setError(dispatch)(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (error[name]) {
      clearError(dispatch)(name);
    }
    setValue(dispatch)(name, (name === 'totalCapacity' ? +value : value));
  };

  const handleToggle = (e) => {
    const { name, checked } = e.target;
    setValue(dispatch)(name, checked);
  };

  const handleSelect = (name, value) => {
    setValue(dispatch)(name, value.map(val => val.value));
  };

  const handleLocalizedInput = language => (e) => {
    const { name, value } = e.target;
    if (error[name]) {
      clearError(dispatch)(name);
    }
    setValueLocalized(dispatch)(name, value, language);
  };

  const handleDate = (name, value) => {
    setValue(dispatch)(name, value);
  };

  const handleImage = language => (imageUrl) => {
    setValueLocalized(dispatch)('image', imageUrl, language);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      saveEvent(data, isEdit ? 'modify' : 'create');
    }
  };

  const handleDeleteEvent = () => {
    handleDelete(data?._id, data?.eventSlugName);
  };

  const getMentors = (input, callback) => {
    callback(mentors.filter(value => value.label.includes(input)));
  };

  useEffect(() => {
    getUsersList({ role: MENTOR_ROLE_MUSIC_MEETS, to: 1000 }).then((res) => {
      const dictionary = res.users.reduce((acc, curr) => ({ ...acc, [curr._id]: curr.email }), {});
      setMentorsDictionary(dictionary);
      setMentors(transformUserOptions(res));
    }).catch(err => console.log(err));
  }, []);

  const transformToSelectMentors = (options) => {
    if (!Array.isArray(options)) return [];
    return options.map(option => ({ label: mentorsDictionary?.[option], value: option }));
  };

  const handleExport = async () => {
    setCreatingLineup(true);
    if (!data.eventSlugName) return;
    axios.get('/export-lineup', { params: { name: data.eventSlugName } }).then((res) => {
      const downloadUrl = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${data.eventSlugName}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setCreatingLineup(false);
      });
  };

  const hasErrors = Object.keys(error).length > 0;

  return (
    <form className="bg-black text-white p-4" onSubmit={handleSubmit}>
      {
        !isEdit ? <Input name="eventName" error={error.eventName} onChange={handleInput} onBlur={checkValidSlugName} label="Event name" value={data?.eventName} required /> : null
      }
      <Spacer />
      <div className="font-americaMonoRegular text-xs mb-4 mt-2">
        Event slugname:
        {' '}
        {`/${data?.eventSlugName || ''}`}
      </div>
      <Select
        className="mb-2"
        name="postSlugNames"
        label="RELATED POSTS"
        value={transformToSelectData(data?.postSlugNames)}
        loadOptions={getPosts}
        onChange={value => handleSelect('postSlugNames', value)}
      />
      <Spacer />
      <Select
        className="mb-2"
        name="eventsArray"
        label="SUB EVENTS"
        value={transformToSelectData(data?.eventsArray)}
        loadOptions={inputValue => getSlugsNames('events', inputValue)}
        onChange={value => handleSelect('eventsArray', value)}
      />
      <Spacer />
      <Select
        className="mb-2"
        name="artistSets"
        label="ARTIST SETS"
        value={transformToSelectData(data?.artistSets)}
        loadOptions={inputValue => getSlugsNames('artistSets', inputValue)}
        onChange={value => handleSelect('artistSets', value)}
      />
      <Spacer />
      <Select
        className="mb-2"
        name="tickets"
        label="TICKETS"
        value={transformToSelectData(data?.tickets)}
        loadOptions={inputValue => getSlugsNames('tickets', inputValue)}
        onChange={value => handleSelect('tickets', value)}
      />
      <Select
        className="mb-2"
        name="mentors"
        label="Mentors (Pro only)"
        cacheOptions
        value={transformToSelectMentors(data?.mentors)}
        loadOptions={getMentors}
        defaultOptions={mentors}
        isLoading={mentors.length === 0}
        onChange={value => handleSelect('mentors', value)}
      />
      <div className="flex w-full gap-4 mb-2">
        <div className="w-full">
          <label className="font-americaMonoRegular uppercase text-xs" htmlFor="genres">
            Genres
            <ReactSelect
              className="font-americaMonoRegular text-black font-bold mt-1"
              isMulti
              id="genres"
              name="genres"
              options={GENRE_OPTIONS}
              value={GENRE_OPTIONS.filter(opt => data?.genres?.includes(opt.value))}
              onChange={value => setValue(dispatch)('genres', value ? value.map(v => v.value) : [])}
            />
          </label>
        </div>
      </div>
      <Spacer />
      <LanguageTabsForms description="ee" title="aa" activeTabStyle="tab active" width="w-full">
        {(activeLanguage, languages) => (
          <div className="bg-greyWithinForm px-4 xxl1:pr-2 py-1 mb-3 rounded-b-lg mr-1 xxl:mr-0">
            {languages.map(lang => (
              <div key={lang} className={`${activeLanguage.toLowerCase() === lang.toLowerCase() ? 'flex flex-nowrap mx-1 flex-row' : 'hidden'}`}>
                <div className="w-1/2 xxl1:w-4/5">
                  <Spacer />
                  <Input name="pretitle" onChange={handleLocalizedInput(lang)} label="Pretitle" value={data?.pretitle?.[lang]} />
                  <Spacer />
                  <Input name="eventReadableName" onChange={handleLocalizedInput(lang)} label="Title" value={data?.eventReadableName?.[lang]} required={lang === 'en'} error={lang === 'en' ? error.eventReadableName : null} />
                  <Spacer />
                  <Input name="subtitle" onChange={handleLocalizedInput(lang)} label="Subtitle" value={data?.subtitle?.[lang]} />
                  <Spacer />
                  <Textarea name="description" onChange={handleLocalizedInput(lang)} label="Description" value={data.description?.[lang]} />
                </div>
                <div className="w-1/2 pl-4">
                  <UIImage activeLanguage={lang} getImageUrl={handleImage(lang)} imageUrl={data.image || {}} />
                </div>
              </div>
            ))}
          </div>
        )}
      </LanguageTabsForms>
      <div className="w-full flex">
        <div className="w-1/3 mr-4">
          <DateAndTimePicker title="Begin Time" name="beginDateTime" handleDate={handleDate} existingDate={formInput?.beginDateTime} action className="w-full" />
        </div>
        <div className="w-1/3 mr-4">
          <DateAndTimePicker title="End Time" name="endDateTime" handleDate={handleDate} existingDate={formInput?.endDateTime} action className="w-full" />
        </div>
        <div className="w-1/3 mt-4">
          <label className="font-americaMonoRegular uppercase text-xs" htmlFor="timezone">
            Timezone
            <ReactSelect
              className="font-americaMonoRegular text-black font-bold mt-1"
              id="timezone"
              name="timezone"
              options={TIMEZONES}
              value={TIMEZONES.find(tz => tz.value === data?.timezone) || null}
              onChange={option => setValue(dispatch)('timezone', option ? option.value : '')}
            />
          </label>
        </div>
      </div>
      <div className="w-1/2">
        <Input name="totalCapacity" onChange={handleInput} label="Capacity" value={data.totalCapacity} type="number" />
      </div>
      <div className="w-1/2 py-4">
        <Input name="salePostSlugName" onChange={handleInput} label="Tickets Page SlugName (fill only for festival events)" value={data?.salePostSlugName} type="text" />
      </div>
      <div className="flex w-full justify-around mb-6">
        <Toggle title="Show date" value={data?.showDate} callback={handleToggle} name="showDate" />
        <Toggle title="Show datetime" value={data?.showDateTime} callback={handleToggle} name="showDateTime" />
        <Toggle title="Show ticket" value={data?.showTicket} callback={handleToggle} name="showTicket" />
        <Toggle title="Show venue" value={data?.showVenue} callback={handleToggle} name="showVenue" />
      </div>
      <div className="flex justify-around my-2">
        <button onClick={handleExport} className={` font-americaMonoBold text-xs px-6 py-2 rounded-full  uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4 bg-${creatingLineup ? 'grey cursor-wait' : 'white'}`} type="button" disabled={creatingLineup}>
          Export
        </button>
        {
          isEdit ? (
            <button type="button" className="bg-red text-white font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4" onClick={() => setShowDeleteModal(true)}>
              Delete
            </button>
          ) : null
        }
        <button
          className={`font-americaMonoBold text-xs px-6 py-2 rounded-full uppercase ml-7 mt-2 w-110px text-black rounded-full py-2 px-4 bg-${hasErrors ? 'gray' : 'white'}`}
          type="submit"
          disabled={hasErrors}
        >
          {isEdit ? 'Save' : 'Create'}
        </button>
      </div>
      {showDeleteModal ? <Delete canItemBeDeleted={canItemBeDeleted} onAccept={handleDeleteEvent} onClose={() => setShowDeleteModal(false)} /> : null}
    </form>
  );
};

const deleteOptions = (canItemBeDeleted, onAccept, onClose) => {
  if (!canItemBeDeleted) {
    return ({
      title: 'Item cannot be deleted',
      description: 'This event has other records linked to it and it cannot be deleted.',
      handleClick: onClose,
      closeToggleDelete: onClose,
    });
  }
  return ({ title: 'Item to be deleted', description: 'Are you sure you want to delete this event?', handleClick: onAccept, closeToggleDelete: onClose });
};

const Delete = ({ canItemBeDeleted, onAccept, onClose }) => {
  const { title, description, handleClick, closeToggleDelete } = deleteOptions(canItemBeDeleted, onAccept, onClose);
  return (
    <DeleteModal zIndex="20" title={title} handleClick={handleClick} closeToggleDelete={closeToggleDelete}>
      {description}
    </DeleteModal>
  );
};

export default EventForm;
